// Cio che il tressette PASSA al trasporto comune (`src/lib/gamehost/`): il `dominio` delle rotte, i posti ammessi, la mossa e come si compone l'azione, le frasi di un rifiuto. Il
// trasporto non conosce nessun gioco: e questo file che glielo dice. La forma e quella della briscola (TMA#746 e ritocchi), con in piu il SEGNO della mossa del 2v2.
//
// `componiAzione` e le liste sono costanti/funzioni DI MODULO (il trasporto tiene l'ultima callback in un ref e una lambda inline non riaprirebbe piu il socket).
import type { PostoPubblico } from '@/lib/gamehost/protocol';
import { creaTavoloBody, restDi } from '@/lib/gamehost/rest';
import type { TipoTavolo } from '@/lib/gamehost/rest';
import type { VociRifiuto } from '@/lib/gamehost/rifiuti';
import { ticketRoute } from '@/lib/gamehost/ticket';
import { MOTIVI } from './protocol';
import type { AzioneDiPersona, Carta, ComeFinita, Posto, Segno } from './protocol';

/** Il segmento di `/v1/games/<dominio>`: `limulo-app/gioco.js` di QTressette, `dominio`. */
export const TRESSETTE_DOMINIO = 'tressette';
/** Un contro uno (col tallone), e due contro due (le coppie {0,2} e {1,3}: SOLO amichevole, L-3437, ADR-0235 §11). */
export const POSTI_1V1 = 2;
export const POSTI_2V2 = 4;
export type PostiTressette = typeof POSTI_1V1 | typeof POSTI_2V2;
/** I posti che il gioco ammette (`POSTI_AMMESSI` di `regole.ts` del game host). */
export const POSTI_AMMESSI: readonly number[] = [POSTI_1V1, POSTI_2V2];

export const TRESSETTE_REST = restDi(TRESSETTE_DOMINIO);
export const TRESSETTE_TICKET_ROUTE = ticketRoute(TRESSETTE_DOMINIO);

/** I tre livelli del robot (`LIVELLI_ROBOT` di `limulo-app/rotte.js` del gioco): il nome visibile e `livello.<L>` nel catalogo. */
export const LIVELLI_ROBOT = ['L0', 'L1', 'L2'] as const;
export type LivelloRobot = (typeof LIVELLI_ROBOT)[number];

/** La mossa di una persona: la carta che gioca e, SOLO se apre la presa nel 2v2, il segno che dice. Il posto lo mette il trasporto, dall'ultimo `table:state`. */
export interface MossaTressette {
  carta: Carta;
  segno?: Segno;
}

/**
 * Le chiavi dell'azione sono ESATTAMENTE `carta`, `posto`, `t` e, solo con un segno, `segno` (`CHIAVI_DELLA_MOSSA[_COL_SEGNO]` di `regole.ts` del game host): un'altra, o `segno: undefined`
 * messo a mano, e `AZIONE_MALFORMATA`. Il segno non si mette mai se non c'e.
 */
export function componiAzione(posto: Posto, da: MossaTressette): AzioneDiPersona {
  return da.segno === undefined ? { t: 'GIOCA', posto, carta: da.carta } : { t: 'GIOCA', posto, carta: da.carta, segno: da.segno };
}

/**
 * Il corpo di un tavolo nuovo: i campi di sempre (`tipo`, `posti`, `idempotencyKey`) e, SOLO se si sceglie un livello, `robot: { livello }`. Col campo `robot` chi crea siede a un posto
 * estratto dal core e OGNI altro posto e un robot del pool (1v1: uno; 2v2: tre). Mai `classificato`: un tavolo creato da qui non conta, e solo a un tavolo che non conta il gioco ammette
 * i robot (`robotAmmessi` di `regole.ts`, ADR-0212 §2.7-2.8). Un tavolo coi robot e SEMPRE casual (un invito non ha senso: nessun posto da riempire).
 */
export function creaTavoloTressetteBody(a: { tipo: TipoTavolo; posti: number; idempotencyKey: string; livello?: LivelloRobot }) {
  const base = creaTavoloBody({ tipo: a.tipo, posti: a.posti, idempotencyKey: a.idempotencyKey });
  return a.livello === undefined ? base : { ...base, robot: { livello: a.livello } };
}

/**
 * Le frasi di un rifiuto: i quattro motivi che le regole dicono in `reason` (carta non in mano, seme obbligatorio, segno non ammesso, segno falso) e i codici di `puòAgire` e del core che ne
 * hanno una loro. L'elenco e CHIUSO: `ILLEGALE` senza un motivo noto e `AZIONE_MALFORMATA` hanno la frase generica.
 */
export const VOCI_RIFIUTO: VociRifiuto = {
  motivi: MOTIVI,
  codici: ['FUORI_TURNO', 'NON_TUO', 'PRESA_DA_RACCOGLIERE', 'STALE_SEQ', 'FINITA', 'NON_DISTRIBUITO', 'THROTTLED'],
};

// COPIA DICHIARATA di src/lib/briscola/gioco.ts @ 97b42cbc0e02b359c98063b8caa6fe3177973976: `eRobotNonDisponibili`, righe 59-66, identica (la zona di un gioco non importa dalla zona di un altro).
/**
 * Il 503 di «Contro i robot»: il core risponde `503 no_robot_available` quando il pool del gioco non ha abbastanza account liberi, e nessun tavolo nasce. Si riconosce dallo status
 * E dal codice (ovunque stia nel corpo dell'errore): un ALTRO 503 (il servizio che non risponde) resta un guasto generico.
 */
export function eRobotNonDisponibili(errore: unknown): boolean {
  const e = errore as { status?: unknown; data?: unknown } | null;
  if (e === null || typeof e !== 'object' || e.status !== 503) return false;
  try {
    return JSON.stringify(e.data ?? null).includes('no_robot_available');
  } catch {
    return false;
  }
}

// COPIA DICHIARATA di src/lib/briscola/gioco.ts @ 97b42cbc0e02b359c98063b8caa6fe3177973976: `lineaDelPosto`, riga 70, identica.
/** La linea di un posto: nel 1v1 ogni posto e una linea, nel 2v2 le coppie sono a posti alterni (0-2 contro 1-3). */
export const lineaDelPosto = (posti: number, posto: Posto): number => (posti === POSTI_2V2 ? posto % 2 : posto);

// COPIA DICHIARATA di src/lib/briscola/disposizione.ts @ 97b42cbc0e02b359c98063b8caa6fe3177973976: `EMOJI_ROBOT` e `nomeMostrato`, righe 26-34, qui `PREFISSO_ROBOT` ed `etichettaDelPosto` (stessa regola: il «🤖» si toglie solo a un posto ROBOT, il criterio e il TIPO, mai il testo).
/** Il prefisso che il game host mette all'etichetta di un robot (kit, `ETICHETTA_BOT`, ADR-0212 §2.1: «🤖 Bot»). La TMA disegna gia il suo `BadgeBot`: l'icona sta una volta sola. */
const PREFISSO_ROBOT = /^\u{1F916}️?\s*/u;

/** L'etichetta di un posto come la zona la stampa: per un posto ROBOT senza il «🤖» iniziale (c'e il `BadgeBot`); per una PERSONA invariata (anche se il suo nickname comincia cosi). */
export function etichettaDelPosto(posto: Pick<PostoPubblico, 'tipo' | 'etichetta'>): string {
  return posto.tipo === 'robot' ? posto.etichetta.replace(PREFISSO_ROBOT, '') : posto.etichetta;
}

/**
 * Il NOME di un posto: SOLO il nickname QLASH che il game host manda in `etichetta` (ADR-0238, I-898, criterio C16 del contratto). ⛔ Mai un ripiego su email o nome anagrafico: di un
 * posto il tavolo legge `tipo` ed `etichetta` e nient'altro. Un posto libero e `libero`; una PERSONA senza nickname e il segnaposto opaco `senzaNome`; un robot senza etichetta e `bot`.
 * Le tre frasi le passa chi chiama (sono del catalogo).
 */
export function nomeDelPosto(posti: readonly PostoPubblico[], p: number, frasi: { libero: string; senzaNome: string; bot: string }): string {
  const pp = posti.find((x) => x.posto === p);
  if (!pp || pp.tipo === 'libero') return frasi.libero;
  const n = etichettaDelPosto(pp).trim();
  if (n !== '') return n;
  return pp.tipo === 'robot' ? frasi.bot : frasi.senzaNome;
}

export const eRobot = (posti: readonly PostoPubblico[], p: number): boolean => posti.find((x) => x.posto === p)?.tipo === 'robot';

/**
 * Come e finita la partita, da `esito.dettaglio` (`{ come, riepiloghi, motivoUscita? }` di `regole.ts` del game host): `vinco-subito` (una linea e arrivata al traguardo a meta smazzata, o con la
 * sua ultima presa), `fine-smazzata`, o `null` (un abbandono, o un dettaglio che non si legge). `dettaglio` e `unknown` nel trasporto: si legge solo il campo `come`, e solo dai due valori noti.
 */
export function comeFinitaOf(esito: { dettaglio?: unknown } | null): ComeFinita {
  const d = esito?.dettaglio;
  if (d === null || typeof d !== 'object' || !('come' in d)) return null;
  const c = (d as { come: unknown }).come;
  return c === 'vinco-subito' || c === 'fine-smazzata' ? c : null;
}
